/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useParams } from 'react-router-dom';
import { Dispatch, Fragment, SetStateAction, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { message } from 'antd';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { GetTenderAttachmentResponse } from '~src/services/api';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { sortByModified, sortByType, TableEntry } from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import {
  DbFocusOptions,
  DbTenderFolder,
  DbAttachment,
  DbShareTenderFolder,
} from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { uniq } from 'lodash';
import { useMutation } from 'react-query';
import {
  FileViewerMargins,
  useFileViewerContext,
} from '~src/contexts/FileToViewContext';
import { formatDate } from '~src/utils/formatDate';
import _ from 'lodash';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { fileIcons } from '~src/config/fileTypeIcons';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';
import { getIsFilePreviewableByIcon } from '~components/atoms/FileTables/FileTable';

export type TendersDocumentsTableUrlParams = {
  tenderId: string;
  rootFolderId?: string;
  currentFolderId?: string;
};

export interface StructuredFoldersTendersDocumentsTableProps
  extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  handleRow: Required<StructuredFoldersTendersDocumentsTableProps>['onRow'];
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
  parentContainerHeight?: string;
  showDeleted?: boolean;
  shareKey?: string;
  shareId?: string;
  tableColumnsConfig?: {
    onGetTenderAttachment?: (params: {
      attachmentId?: string;
    }) => Promise<GetTenderAttachmentResponse>;
    onGetTenderVersionedFile?: (params: {
      attachmentId?: string;
      versionId?: string;
      currentFolderId?: string;
      rootFolderId?: string;
    }) => Promise<GetTenderAttachmentResponse>;
  };
  attachmentsList?: DbAttachment[];
  foldersList?: (DbTenderFolder | DbShareTenderFolder)[];
  isListLoading: boolean;
  isOnMyDocumentsTable?: boolean;
}

/** Default StructuredFoldersTendersDocumentsTable, used with folder navigation */
export const StructuredFoldersTendersDocumentsTable: React.FC<StructuredFoldersTendersDocumentsTableProps> = ({
  onSelect,
  onSelectAll,
  handleRow,
  setSelectedItems,
  onEmptyOverlay,
  selectedKeys,
  highlightedRow,
  suMode,
  shareKey,
  shareId,
  focusOptions,
  parentContainerHeight,
  showDeleted,
  tableColumnsConfig,
  attachmentsList,
  foldersList,
  isListLoading,
  isOnMyDocumentsTable,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags;
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { fileToView, setFileToView } = useFileViewerContext();

  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl({
    isOnShare: isOnMyDocumentsTable || false,
  });

  const isFirstLevel = !currentFolderId;

  const currentFolder = useCurrentFolder(foldersList);
  const permissionLevel = currentFolder?.permission || 0;

  const [doGetTenderAttachment] = useMutation(
    tableColumnsConfig?.onGetTenderAttachment
      ? tableColumnsConfig?.onGetTenderAttachment
      : async () => null,
    {
      onError: () => {
        message.error(intl.formatMessage({ id: 'loading-attachment-error' }));
      },
      onSuccess: (data) => {
        if (data) {
          setFileToView(data, { marginTop: FileViewerMargins.TABS });
        }
      },
    }
  );

  const [doGetTenderVersionedFile] = useMutation(
    tableColumnsConfig?.onGetTenderVersionedFile
      ? tableColumnsConfig?.onGetTenderVersionedFile
      : async () => null,
    {
      onError: () => {
        message.error(intl.formatMessage({ id: 'loading-attachment-error' }));
      },
      onSuccess: (data) => {
        if (data) {
          setFileToView(data, { marginTop: FileViewerMargins.TABS });
        }
      },
    }
  );

  const searchContext = useContext(SearchContext);

  const foldersSource = useMemo(() => {
    const foldersData =
      !isFirstLevel && foldersList
        ? foldersList.filter((item) =>
            'parent_id' in item
              ? String(item.parent_id) === currentFolderId
              : false
          )
        : foldersList;

    return (
      foldersData?.map((folder) =>
        dbTenderToTableEntry(
          (folder as unknown) as DbTenderFolder,
          isOnMyDocumentsTable
            ? isFirstLevel
              ? (folder as DbTenderFolder).id
              : rootFolderId
            : foldersList?.[0]?.id
        )
      ) || []
    );
  }, [foldersList, currentFolderId]);

  const filteredFolders = [
    ...foldersSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          String(item.dbFile?.versions?.[0]?.version ?? ''),
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const filesSource = useMemo(
    () => (attachmentsList || []).map(dbFAttachmentToTableEntry),
    [attachmentsList]
  );

  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el: any) =>
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          String(
            dynamicIndex > 0
              ? item.dbFile?.versions?.[dynamicIndex]?.version ?? ''
              : item.dbFile?.versions?.[0]?.version ?? ''
          ),
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if ((record.key === highlightedRow?.key && data) || record.disabled)
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  // SVF Viewer
  const getCurrentFileVersion = (record: TableEntry) => {
    if (isFirstLevel) return record.dbAttachment;

    if (!record.dbFile) return undefined;

    const selectedVersionId = fileVersionsContext[record.id]?.id;

    return (
      record.dbFile.versions?.find(
        (version: any) => String(version.id) === String(selectedVersionId)
      ) ||
      fileVersionsContext[record.id] ||
      record.dbFile.versions?.[0]
    );
  };

  const getFileSVFVersion = (record: TableEntry) => {
    const currentVersion = getCurrentFileVersion(record);

    if (!currentVersion) return undefined;

    return {
      ...currentVersion,
      name:
        currentVersion.name ||
        record.name ||
        record.dbAttachment?.title ||
        record.dbAttachment?.name ||
        '',
      version:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      revision:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      updated_at: currentVersion.updated_at || record.updatedAt,
      description:
        currentVersion.registration_instance_description ??
        currentVersion.description ??
        record.dbFile?.description ??
        record.description ??
        '',
      registration_instance_description:
        currentVersion.registration_instance_description ?? '',
      extension_svf_model:
        currentVersion.extension_svf_model ??
        record.dbFile?.versions?.[0]?.extension_svf_model ??
        null,
      is_svf_convertible:
        currentVersion.is_svf_convertible ??
        record.dbFile?.versions?.[0]?.is_svf_convertible ??
        false,
    };
  };

  const getFileSVFState = (record: TableEntry) =>
    getFileSVFVersion(record)?.extension_svf_model?.state;

  const getVendorShareRootSVFFile = (record: TableEntry) => {
    if (!record.dbAttachment) return undefined;

    const currentVersion = getFileSVFVersion(record);

    if (!currentVersion || currentVersion.extension_svf_model?.state !== 7) {
      return undefined;
    }

    return {
      ...record.dbAttachment,
      sourceType: 'tender-share-root-svf',
      share_id: shareId,
      share_key: shareKey,
      file_guid: String(record.dbAttachment.id),
      title:
        currentVersion.name ||
        record.dbAttachment.title ||
        record.dbAttachment.name ||
        record.name,
      file_name:
        currentVersion.name ||
        record.dbAttachment.title ||
        record.dbAttachment.name ||
        record.name,
      extension_svf_model: currentVersion.extension_svf_model || null,
      is_svf_convertible: currentVersion.is_svf_convertible === true,
      updated_at: currentVersion.updated_at || record.updatedAt,
      description:
        currentVersion.registration_instance_description ??
        currentVersion.description ??
        record.dbAttachment.description ??
        '',
    };
  };

  const getVendorShareFolderSVFFile = (record: TableEntry) => {
    if (!record.dbAttachment) return undefined;

    const currentVersion = getFileSVFVersion(record);

    if (!currentVersion || currentVersion.extension_svf_model?.state !== 7) {
      return undefined;
    }

    return {
      ...record.dbAttachment,
      sourceType: 'tender-share-inside-svf',
      share_id: shareId,
      share_key: shareKey,
      root_folder_id: Number(rootFolderId),
      folder_id: Number(currentFolderId || rootFolderId),
      versioned_file_id: Number(
        currentVersion.registration_id ||
          currentVersion.versioned_file_id ||
          record.dbAttachment.id
      ),
      file_guid: String(
        currentVersion.file_id || currentVersion.file_guid || currentVersion.id
      ),
      title:
        currentVersion.name ||
        record.dbAttachment.title ||
        record.dbAttachment.name ||
        record.name,
      file_name:
        currentVersion.name ||
        record.dbAttachment.title ||
        record.dbAttachment.name ||
        record.name,
      extension_svf_model: currentVersion.extension_svf_model || null,
      is_svf_convertible: currentVersion.is_svf_convertible === true,
      serial_number:
        currentVersion.serial_number ??
        currentVersion.version ??
        currentVersion.revision ??
        1,
      updated_at: currentVersion.updated_at || record.updatedAt,
      description:
        currentVersion.registration_instance_description ??
        currentVersion.description ??
        record.dbAttachment.description ??
        '',
    };
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: '',
        align: 'center',
        width: 30,
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) => {
                  e.preventDefault();
                  const customEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(customEvent);
                }}
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 75,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.folder;
          const isPreviewableFormat = getIsFilePreviewableByIcon(type.icon);
          const ext = record.extension?.toLowerCase() || '';
          const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

          const fileVersion = getFileSVFVersion(record);
          const fileState = getFileSVFState(record);

          const canShowSVFActionIcon =
            !isTenderFolder &&
            !!record.dbAttachment &&
            !!fileVersion?.extension_svf_model &&
            Number(fileState) === 7;

          const cadDisplayVersion = isCadFile
            ? ({
                ...(fileVersion || {}),
                name: fileVersion?.name || record.name || '',
                extension_svf_model: fileVersion?.extension_svf_model ?? null,
                is_svf_convertible: fileVersion?.is_svf_convertible === true,
              } as any)
            : null;

          const handlePreviewClick = (e: any) => {
            e.stopPropagation();

            let file =
              record.dbAttachment?.files?.[
                record.dbAttachment?.files.length - 1
              ];

            if (!isFirstLevel && record.dbFile) {
              const versionId = fileVersionsContext[record.id]?.id;
              file =
                record.dbFile.versions?.find(
                  (v: any) => String(v.id) === String(versionId)
                ) ||
                record.dbFile.versions?.[0] ||
                file;
            }

            if (!record.dbAttachment?.id) {
              return message.error(
                intl.formatMessage({
                  id: 'file-preview-error',
                })
              );
            }

            if (isFirstLevel) {
              doGetTenderAttachment({
                attachmentId: String(record.dbAttachment.id),
              });
            } else if (file) {
              doGetTenderVersionedFile({
                attachmentId: String(
                  file?.file_id || file?.file_guid || file?.id
                ),
                versionId: String(
                  file?.registration_id ||
                    file?.versioned_file_id ||
                    record?.dbAttachment.id
                ),
                rootFolderId,
                currentFolderId,
              });
            } else {
              message.error(
                intl.formatMessage({
                  id: 'file-preview-error',
                })
              );
            }
          };

          const handleVendorSVFClick = (e?: any) => {
            e?.stopPropagation?.();

            if (!record.dbAttachment) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            const svfBridgeFile = isFirstLevel
              ? getVendorShareRootSVFFile(record)
              : getVendorShareFolderSVFFile(record);

            if (!svfBridgeFile) {
              message.error(intl.formatMessage({ id: 'file-preview-error' }));
              return;
            }

            setFileToView(svfBridgeFile as any, {
              marginTop: FileViewerMargins.TABS,
            });
          };

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: isTenderFolder ? (
              <div css={styles.outerIconWrapper}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}>
                  <NavIcon
                    customStyle={{ width: 30, height: 30 }}
                    icon={getTenderIconArichive({
                      is_sub_folder: !isFirstLevel,
                    })}
                  />
                </div>
              </div>
            ) : (
              <div css={styles.outerIconWrapper}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}>
                    {isPreviewableFormat &&
                      ((isFirstLevel &&
                        !!tableColumnsConfig?.onGetTenderAttachment) ||
                        (!isFirstLevel &&
                          !!tableColumnsConfig?.onGetTenderVersionedFile)) && (
                        <div css={styles.iconContainer}>
                          <IconButton
                            click={handlePreviewClick}
                            iconStyle={type.iconStyle}
                            icon={['far', 'eye']}
                            color="red"
                          />
                        </div>
                      )}

                    {canShowSVFActionIcon && (
                      <SVFIconType
                        key={`vendor-svf-${record.id}-${fileState ?? 'none'}`}
                        version={fileVersion as any}
                        extension={record.extension}
                        variant="sidebar"
                        disabled={false}
                        customTooltip={intl.formatMessage({
                          id: 'svf-view-file-window',
                        })}
                        onClick={handleVendorSVFClick}
                        type={type}
                      />
                    )}

                    {isCadFile ? (
                      <SVFIconType
                        key={`vendor-cad-file-icon-${record.id}`}
                        version={cadDisplayVersion}
                        extension={record.extension}
                        variant="table"
                        customTooltip={intl.formatMessage(
                          { id: type.icon },
                          { type: record.extension?.toUpperCase() }
                        )}
                        onClick={() => {
                          // icon only
                        }}
                        type={type}
                      />
                    ) : (
                      <IconButton
                        iconStyle={type.iconStyle}
                        icon={type.icon}
                        iconSecond={
                          record.state === DB_STATE.REMOVED
                            ? ['fad', 'trash']
                            : null
                        }
                        colorSecondIcon={'red'}
                        customStyleSecondIcon={{ left: '12px', top: '10px' }}
                        color={type.color}
                        customStyle={styles.icons}
                        tooltip={intl.formatMessage(
                          { id: type.icon },
                          { type: record.extension?.toUpperCase() }
                        )}
                        badge={
                          record.state !== DB_STATE.REMOVED &&
                          type.is_empty &&
                          '0'
                        }
                        backgroundOff
                      />
                    )}
                  </div>
                </div>
              </div>
            ),
          };
        },
        sorter: sortByType,
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {record.dbTenderFolder?.options?.alias_title || name}
                {record.favourite && (
                  <IconButton
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children:
              (!isFirstLevel
                ? fileVersionsContext[record.id]
                    ?.registration_instance_description
                : null) ??
              (!isFirstLevel
                ? fileVersionsContext[record.id]?.description
                : null) ??
              record.dbFile?.description ??
              record.description,
          };
        },
      },
      //@ts-ignore
      ...(!isFirstLevel
        ? [
            {
              title: intl.formatMessage({ id: 'ver' }),
              dataIndex: 'dbFile',
              width: 80,
              align: 'center',
              onCell: () => ({ style: { padding: '4px 10px' } }),
              render: (file: any, element: any) => {
                if (element.folder) return null;
                return {
                  props: {
                    style: getBackgroundRow(element),
                  },
                  children: file && <VersionSelect file={file} />,
                };
              },
            },
          ]
        : []),
      {
        title: intl.formatMessage({ id: 'modified-date' }),
        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),
        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          if (!isFirstLevel && record.dbFile?.versions) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile.versions[0]?.updated_at ||
              updatedAt;
          }

          let displayDate = '-';
          if (modified) {
            const formatted = formatDate(modified, 'short');
            displayDate =
              formatted.toLowerCase() === 'unknown' ? '-' : formatted;
          }

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: displayDate,
          };
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      tagsData,
      currentFolderId,
      isFirstLevel,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  // const selectionDisabled = permissionLevel < RIGHTS_LEVEL.OPEN;

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder   // JIRA: MVP TV1-362
  const selectionDisabled =
    !isOnMyDocumentsTable && permissionLevel < RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // Always allow selection in My Documents (Shared Tenders)
    if (isOnMyDocumentsTable) return false; // JIRA: MVP TV1-362

    // if root, use folder permission
    if (currentFolderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return selectionDisabled || record.disabled;
  };

  const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFolders, ...filteredFiles];
  };

  const content = (
    <Fragment>
      <Table<TableEntry>
        //  scroll={{ x: '100%', y: '50%' }} // Add this line
        css={[styles.table]}
        showSorterTooltip={false}
        className={`main-content-window ${
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          'timeMachine-table'
        } ${suMode && 'su-table'}`}
        pagination={false}
        bordered
        columns={columns}
        dataSource={[...filteredFolders, ...filteredFiles]}
        loading={!shareKey || isListLoading}
        rowClassName={(record) => {
          let className = suMode ? 'su-row-table ' : '';
          if (window.sessionStorage.getItem('appState') === 'timeMachine') {
            className += 'timeMachine-row-table' + ' ';
          }
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          renderCell: (type, record, element, node) => {
            return {
              props: {
                style: getBackgroundRow(record),
              },
              children: node,
            };
          },
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: isRecordSelectDisabled(record),
          }),
        }}
        onRow={(record, index) => ({
          ...handleRow(record, index),
          onClick: (event) => {
            if (event.shiftKey) {
              return handleSelectShift(record, index);
            }
            const onClickAction = handleRow(record, index).onClick;
            if (onClickAction) {
              return onClickAction(event);
            }
          },
        })}
        {...tableProps}
      />
      <div
        onContextMenu={() => {
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  if (fileToView) {
    return (
      <div
        style={{
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          display: 'flex',
          flexDirection: 'column',
          width: '1230px',
        }}>
        {content}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
      }}>
      <div
        style={{
          minWidth: 1150,
          // width: 1065,
          height: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 42px)`,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}>
        {content}
      </div>
    </div>
  );
};

export const dbTenderToTableEntry = (
  dbTender: DbTenderFolder,
  firstElementId?: string | number,
  isUnassigned?: boolean
): TableEntry => {
  const {
    id,
    name,
    description,
    parent_id,
    created_at,
    updated_at,
    state,
  } = dbTender;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    name: name || 'Unknown',
    description,
    notifications: Boolean(false),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(parent_id),
    id,
    state,
    disabled: state !== DB_STATE.AVAILABLE,
    favourite: Boolean(false),
    isTenderDocuments: true,
    dbTenderFolder: dbTender,
    firstElementId,
    isUnassigned,
  };
};

export const dbFAttachmentToTableEntry = (
  dbAttachment: DbAttachment
): TableEntry => {
  const {
    id,
    name,
    title,
    created_at,
    state,
    updated_at,
    registration_id,
    description,
  } = dbAttachment;
  const nameSections = name ? name.split('.') : title && title.split('.');
  const extension =
    nameSections && nameSections.length
      ? nameSections[nameSections.length - 1].toLowerCase()
      : undefined;

  const stableVersions = (dbAttachment.files || [])
    .map((f: any) => ({
      ...f,
      revision: f.serial_number ?? f.version ?? f.revision,
      version: f.serial_number ?? f.version ?? f.revision,
      id: String(f.registration_instance_id || f.file_id || f.id),
    }))
    .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

  return {
    key: `file-${id}`,
    folder: false,
    moduleFile: false,
    type: (fileIcons[extension || 'default'] ||
      fileIcons.default) as typeof fileIcons.docx,
    extension,
    name: name || String(title),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(registration_id),
    id: Number(id),
    state,
    description: description ? String(description) : '',
    disabled: state !== DB_STATE.AVAILABLE,
    dbAttachment,
    dbFile: { ...dbAttachment, versions: stableVersions } as any,
  };
};
